import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ACCESS_TOKEN, getCookie, REFRESH_TOKEN, removeCookie } from '@utils/cookie';
import {
  getSaleMaterialFromSaleQuotationApi,
  getUserProfileApi,
  getUserSaleItemsApi,
  patchCompanyNameApi,
  patchRegacyUserApi,
  patchUserCompanyDepartmentApi,
  patchUserCompanyInfoApi,
  patchUserCompanyTitleApi,
  patchUserMarketingApprovalApi,
  patchUserNameApi,
  patchUserPasswordApi,
  patchUserPhoneApi,
  patchUserSaleItemsApi,
  patchUserSnsApprovalApi,
  removeColleagueApi,
  userColleagueChangeRepresentativeApi,
  userColleagueGetColleagueListApi,
  userInvitationAcceptApi,
  userInvitationCancelApi,
  userInvitationIsServiceUserApi,
  userInvitationRejectApi,
  userInvitationSendApi,
  userInvitationSetRepresentativeApi,
  userSignOutApi,
  userSupplierProductOptionApi,
} from '@apis/user';
import { MONTH } from '@constants/time';
import {
  UserGetUserProfileData,
  UserGetUserProfileError,
  UserProfileApprovalPatchUserMarketingApprovalError,
  UserProfileApprovalPatchUserMarketingApprovalPayload,
  UserProfileApprovalPatchUserSnsApprovalError,
  UserProfileApprovalPatchUserSnsApprovalPayload,
  UserProfileCompanyPatchUserCompanyDepartmentError,
  UserProfileCompanyPatchUserCompanyDepartmentPayload,
  UserProfileCompanyPatchUserCompanyInfoError,
  UserProfileCompanyPatchUserCompanyInfoPayload,
  UserProfileCompanyPatchUserCompanyTitleError,
  UserProfileCompanyPatchUserCompanyTitlePayload,
  UserProfileDeleteUserWithdrawError,
  UserProfilePatchRegacyUserInfoPayload,
  UserProfilePatchUserNameError,
  UserProfilePatchUserNamePayload,
  UserProfilePatchUserPasswordError,
  UserProfilePatchUserPasswordPayload,
  UserProfilePatchUserPhoneError,
  UserProfilePatchUserPhonePayload,
  UserSaleItemsPartialUpdateError,
  UserSaleItemsReadError,
  UserSaleQuotationGetSaleMaterialsFromSaleQuotationError,
  UserInvitationAcceptUserInvitationData,
  UserInvitationCancelUserInvitationData,
  UserInvitationCancelUserInvitationPayload,
  UserInvitationIsServiceUserData,
  UserInvitationIsServiceUserPayload,
  UserInvitationRejectUserInvitationData,
  UserInvitationSendUserInvitationData,
  UserInvitationSendUserInvitationPayload,
  UserInvitationSetRepresentativeData,
  UserProfilePatchCompanyNamePayload,
} from 'models/swagger/data-contracts';
import { Toast } from '@components/_atoms/Toast';
import {
  UserSaleItemsPartialUpdatePayloadType,
  UserSaleQuotationGetSaleMaterialsFromSaleQuotationPayload,
} from 'models/Supplement';
import { useRouter } from 'next/router';
import { REGACY } from '@constants/routes/routes';
import { CommonAxiosErrorType } from 'models/SupplementType';

/**
 * 회원정보 가져오기 Fetch
 * @function useGetUserProfileFetch
 */
export const useGetUserProfileFetch = () => {
  const accessToken = getCookie(ACCESS_TOKEN);

  const {
    data: getUserProfileFetchData,
    refetch: getUserProfileRefetch,
    isLoading: getUserProfileIsLoading,
    isError: getUserProfileIsError,
  } = useQuery(
    ['user'],
    async function () {
      const result = await getUserProfileApi();
      return result?.data.data as UserGetUserProfileData['data'];
    },
    {
      staleTime: MONTH * 1000, // 30일 동안 fresh 상태 유지 (ms 단위)
      enabled: !!accessToken,
      onError: (err: AxiosError<UserGetUserProfileError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
          const currentDomain = window.location.hostname;

          removeCookie(ACCESS_TOKEN, { domain: currentDomain, path: '/' });
          removeCookie(REFRESH_TOKEN, { domain: currentDomain, path: '/' });
        }
      },
    },
  );

  const isCustomLoading = (!getUserProfileFetchData || getUserProfileIsLoading) && !getUserProfileIsError;

  return {
    getUserProfileFetchData,
    getUserProfileRefetch,
    isCustomLoading,
    getUserProfileIsLoading,
  };
};

/**
 * 유저 회원탈퇴 Fetch
 * @function useUserSignOutFetch
 */
export const useUserSignOutFetch = () => {
  const {
    mutate: userSignOutFetchMutate,
    status: userSignOutFetchStatus,
    isLoading: userSignOutFetchIsLoading,
  } = useMutation(['userSignOut'], () => userSignOutApi(), {
    onError: (err: AxiosError<UserProfileDeleteUserWithdrawError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    userSignOutFetchMutate,
    userSignOutFetchStatus,
    userSignOutFetchIsLoading,
  };
};

/**
 *  비밀번호 변경 Fetch
 *  @function usePatchUserPasswordFetch
 */
export const usePatchUserPasswordFetch = () => {
  const {
    mutate: patchUserPasswordFetchMutate,
    status: patchUserPasswordFetchStatus,
    isLoading: patchUserPasswordFetchIsLoading,
  } = useMutation(['user'], ({ password }: UserProfilePatchUserPasswordPayload) => patchUserPasswordApi({ password }), {
    onError: (err: AxiosError<UserProfilePatchUserPasswordError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    patchUserPasswordFetchMutate,
    patchUserPasswordFetchStatus,
    patchUserPasswordFetchIsLoading,
  };
};

/**
 *  회사명 변경 Fetch
 *  @function usePatchCompanyNameFetch
 */
export const usePatchCompanyNameFetch = () => {
  const {
    mutate: patchCompanyNameFetchMutate,
    status: patchCompanyNameFetchStatus,
    isLoading: patchCompanyNameFetchIsLoading,
  } = useMutation(['user'], ({ company_name }: UserProfilePatchCompanyNamePayload) => patchCompanyNameApi({ company_name }), {
    onError: (err: AxiosError<UserProfilePatchUserNameError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    patchCompanyNameFetchMutate,
    patchCompanyNameFetchStatus,
    patchCompanyNameFetchIsLoading,
  };
};

/**
 *  이름 변경 Fetch
 *  @function usePatchUserNameFetch
 */
export const usePatchUserNameFetch = () => {
  const {
    mutate: patchUserNameFetchMutate,
    status: patchUserNameFetchStatus,
    isLoading: patchUserNameFetchIsLoading,
  } = useMutation(['user'], ({ name }: UserProfilePatchUserNamePayload) => patchUserNameApi({ name }), {
    onError: (err: AxiosError<UserProfilePatchUserNameError>) => {
      if (err.response?.data?.message) {
        Toast.error(err.response.data.message.text);
      }
    },
  });

  return {
    patchUserNameFetchMutate,
    patchUserNameFetchStatus,
    patchUserNameFetchIsLoading,
  };
};

/**
 *  휴대폰 번호 변경 Fetch
 *  @function usePatchUserPhoneFetch
 */
export const usePatchUserPhoneFetch = () => {
  const {
    mutate: patchUserPhoneFetchMutate,
    status: patchUserPhoneFetchStatus,
    isLoading: patchUserPhoneFetchIsLoading,
  } = useMutation(
    ['user'],
    ({ phone_code, phone }: UserProfilePatchUserPhonePayload) => patchUserPhoneApi({ phone_code, phone }),
    {
      onError: (err: AxiosError<UserProfilePatchUserPhoneError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserPhoneFetchMutate,
    patchUserPhoneFetchStatus,
    patchUserPhoneFetchIsLoading,
  };
};

/**
 *  소속 부서 변경 Fetch
 *  @function usePatchUserCompanyDepartmentFetch
 */
export const usePatchUserCompanyDepartmentFetch = () => {
  const {
    mutate: patchUserCompanyDepartmentFetchMutate,
    status: patchUserCompanyDepartmentFetchStatus,
    isLoading: patchUserCompanyDepartmentFetchIsLoading,
  } = useMutation(
    ['user'],
    ({ company_department }: UserProfileCompanyPatchUserCompanyDepartmentPayload) =>
      patchUserCompanyDepartmentApi({ company_department }),
    {
      onError: (err: AxiosError<UserProfileCompanyPatchUserCompanyDepartmentError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserCompanyDepartmentFetchMutate,
    patchUserCompanyDepartmentFetchStatus,
    patchUserCompanyDepartmentFetchIsLoading,
  };
};

/**
 *  직함 변경 Fetch
 *  @function usePatchUserCompanyTitleFetch
 */
export const usePatchUserCompanyTitleFetch = () => {
  const {
    mutate: patchUserCompanyTitleFetchMutate,
    status: patchUserCompanyTitleFetchStatus,
    isLoading: patchUserCompanyTitleFetchIsLoading,
  } = useMutation(
    ['user'],
    ({ company_title }: UserProfileCompanyPatchUserCompanyTitlePayload) => patchUserCompanyTitleApi({ company_title }),
    {
      onError: (err: AxiosError<UserProfileCompanyPatchUserCompanyTitleError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserCompanyTitleFetchMutate,
    patchUserCompanyTitleFetchStatus,
    patchUserCompanyTitleFetchIsLoading,
  };
};

/**
 *  회사 정보 변경 Fetch
 *  @function usePatchUserCompanyInfoFetch
 */
export const usePatchUserCompanyInfoFetch = () => {
  const {
    mutate: patchUserCompanyInfoFetchMutate,
    status: patchUserCompanyInfoFetchStatus,
    isLoading: patchUserCompanyInfoFetchIsLoading,
  } = useMutation(
    ['user'],
    ({
      company_name,
      company_registration_number,
      company_address,
      company_ceo,
    }: UserProfileCompanyPatchUserCompanyInfoPayload) =>
      patchUserCompanyInfoApi({ company_name, company_registration_number, company_address, company_ceo }),
    {
      onError: (err: AxiosError<UserProfileCompanyPatchUserCompanyInfoError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserCompanyInfoFetchMutate,
    patchUserCompanyInfoFetchStatus,
    patchUserCompanyInfoFetchIsLoading,
  };
};

/**
 *  유저 마케팅 정보 수신 동의 여부 변경 Fetch
 *  @function usePatchUserMarketingApprovalFetch
 */
export const usePatchUserMarketingApprovalFetch = () => {
  const {
    mutate: patchUserMarketingApprovalFetchMutate,
    status: patchUserMarketingApprovalFetchStatus,
    isLoading: patchUserMarketingApprovalFetchIsLoading,
  } = useMutation(
    ['user'],
    ({ marketing_approval }: UserProfileApprovalPatchUserMarketingApprovalPayload) =>
      patchUserMarketingApprovalApi({ marketing_approval }),
    {
      onError: (err: AxiosError<UserProfileApprovalPatchUserMarketingApprovalError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserMarketingApprovalFetchMutate,
    patchUserMarketingApprovalFetchStatus,
    patchUserMarketingApprovalFetchIsLoading,
  };
};

/**
 *  유저 SNS(알림톡, 문자) 받기 여부 변경 Fetch
 *  @function usePatchUserSnsApprovalFetch
 */
export const usePatchUserSnsApprovalFetch = () => {
  const {
    mutate: patchUserSnsApprovalFetchMutate,
    status: patchUserSnsApprovalFetchStatus,
    isLoading: patchUserSnsApprovalFetchIsLoading,
  } = useMutation(
    ['user'],
    ({ sns_approval }: UserProfileApprovalPatchUserSnsApprovalPayload) => patchUserSnsApprovalApi({ sns_approval }),
    {
      onError: (err: AxiosError<UserProfileApprovalPatchUserSnsApprovalError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchUserSnsApprovalFetchMutate,
    patchUserSnsApprovalFetchStatus,
    patchUserSnsApprovalFetchIsLoading,
  };
};

/**
 *  v2 유저 추가 정보 변경 Fetch
 *  @function usePatchRegacyUserFetch
 */
export const usePatchRegacyUserFetch = () => {
  const { mutate: patchRegacyUserFetchMutate } = useMutation(
    ['regacyUser'],
    ({
      company_name,
      company_registration_number,
      company_address,
      company_ceo,
      name,
      phone_code,
      phone,
    }: UserProfilePatchRegacyUserInfoPayload) =>
      patchRegacyUserApi({
        company_name,
        company_registration_number,
        company_address,
        company_ceo,
        name,
        phone_code,
        phone,
      }),
    {
      onError: (err: AxiosError<UserProfilePatchUserNameError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    patchRegacyUserFetchMutate,
  };
};

/**
 * 유저 자신의 판매 알림 기능 자재 정보를 조회한다.
 * @function useGetUserSaleItemsFetch
 */
export const useGetUserSaleItemsFetch = () => {
  const accessToken = getCookie(ACCESS_TOKEN);
  const router = useRouter();

  const { data: userSaleItemsFetchData, isLoading: userSaleItemsFetchIsLoading } = useQuery(
    ['userSaleItems'],
    async () => {
      const result = await getUserSaleItemsApi();

      if (result.data.mcode === 2008) {
        router.push(REGACY);
      }
      return result.data.data;
    },
    {
      enabled: !!accessToken,
      onError: (err: AxiosError<UserSaleItemsReadError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return { userSaleItemsFetchData, userSaleItemsFetchIsLoading };
};

/**
 * 유저 자신의 판매 알림 기능 자재 정보를 수정한다.
 * @function usePatchUserSaleItemsFetch
 */
export const usePatchUserSaleItemsFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: patchUserSaleItemsFetchMutate } = useMutation(
    ['patchUserSaleItems'],
    async ({
      is_user_sale_material_push,
      delete_sale_material_list,
      create_sale_material_list,
    }: UserSaleItemsPartialUpdatePayloadType) => {
      await patchUserSaleItemsApi({ is_user_sale_material_push, delete_sale_material_list, create_sale_material_list });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userSaleItems']);
        queryClient.invalidateQueries(['user']);
      },
      onError: (err: AxiosError<UserSaleItemsPartialUpdateError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  return {
    patchUserSaleItemsFetchMutate,
  };
};

/**
 * 유저가 판매용 견적서 요청 후, 해당 견적서 내의 등록한 아이템들을 판매 알림 기능 자재 정보 형식으로 조회한다.
 * @function useGetSaleMaterialFromSaleQuotationFetch
 */
export const useGetSaleMaterialFromSaleQuotationFetch = ({
  rfq_number_id,
}: UserSaleQuotationGetSaleMaterialsFromSaleQuotationPayload) => {
  const { data: getSaleMaterialFromSaleQuotationFetch } = useQuery(
    ['getSaleMaterialFromSaleQuotation', rfq_number_id],
    async () => {
      const result = await getSaleMaterialFromSaleQuotationApi({ rfq_number_id });
      return result.data.data;
    },
    {
      enabled: !!rfq_number_id,
      onError: (err: AxiosError<UserSaleQuotationGetSaleMaterialsFromSaleQuotationError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return { getSaleMaterialFromSaleQuotationFetch };
};

/**
 * 사용자 초대 수락 API
 * @function useUserInvitationAcceptFetch
 */
export const useUserInvitationAcceptFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: userInvitationAcceptFetchMutate, isLoading: userInvitationAcceptIsLoading } = useMutation(
    ['userInvitationAccept'],
    async ({ token }: { token: string }) => {
      const result = await userInvitationAcceptApi({ token });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userInvitationAccept']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationAcceptFetchMutate,
    userInvitationAcceptIsLoading,
  };
};

/**
 * 사용자 초대 취소 API
 * @function useUserInvitationCancelFetch
 */
export const useUserInvitationCancelFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: userInvitationCancelFetchMutate, isLoading: userInvitationCancelIsLoading } = useMutation(
    ['userInvitationCancel'],
    async ({ receiver_id }: UserInvitationCancelUserInvitationPayload) => {
      const result = await userInvitationCancelApi({ receiver_id });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userInvitationCancel']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationCancelFetchMutate,
    userInvitationCancelIsLoading,
  };
};

/**
 * 사용자 초대 서비스 사용자 확인 API
 * @function useUserInvitationIsServiceUserFetch
 */
export const useUserInvitationIsServiceUserFetch = () => {
  const { mutate: userInvitationIsServiceUserFetchMutate, isLoading: userInvitationIsServiceUserIsLoading } = useMutation(
    ['userInvitationIsServiceUser'],
    async ({ token }: UserInvitationIsServiceUserPayload) => {
      const result = await userInvitationIsServiceUserApi({ token });
      return result;
    },
    {
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationIsServiceUserFetchMutate,
    userInvitationIsServiceUserIsLoading,
  };
};

/**
 * 사용자 초대 거절 API
 * @function useUserInvitationRejectFetch
 */
export const useUserInvitationRejectFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: userInvitationRejectFetchMutate, isLoading: userInvitationRejectIsLoading } = useMutation(
    ['userInvitationReject'],
    async ({ token }: { token: string }) => {
      const result = await userInvitationRejectApi({ token });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userInvitationReject']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationRejectFetchMutate,
    userInvitationRejectIsLoading,
  };
};

/**
 * 사용자 초대 발송 API
 * @function useUserInvitationSendFetch
 */
export const useUserInvitationSendFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: userInvitationSendFetchMutate, isLoading: userInvitationSendIsLoading } = useMutation(
    ['userInvitationSend'],
    async ({ email_list }: UserInvitationSendUserInvitationPayload) => {
      const result = await userInvitationSendApi({ email_list });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userInvitationSend']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationSendFetchMutate,
    userInvitationSendIsLoading,
  };
};

/**
 * 사용자 초대 대표자 설정 API
 * @function useUserInvitationSetRepresentativeFetch
 */
export const useUserInvitationSetRepresentativeFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: userInvitationSetRepresentativeFetchMutate, isLoading: userInvitationSetRepresentativeIsLoading } = useMutation(
    ['userInvitationSetRepresentative'],
    async () => {
      const result = await userInvitationSetRepresentativeApi();
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userInvitationSetRepresentative']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userInvitationSetRepresentativeFetchMutate,
    userInvitationSetRepresentativeIsLoading,
  };
};

/**
 * 동료 삭제 API
 * @function useRemoveColleagueFetch
 */
export const useRemoveColleagueFetch = () => {
  const queryClient = useQueryClient();

  const { mutate: removeColleagueFetchMutate, isLoading: removeColleagueIsLoading } = useMutation(
    ['removeColleague'],
    async ({ colleague_id, substitute_id }: { colleague_id: string; substitute_id: string }) => {
      const result = await removeColleagueApi({ colleague_id, substitute_id });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    removeColleagueFetchMutate,
    removeColleagueIsLoading,
  };
};

/**
 * 동료 대표자 변경 API
 * @function useSetUserColleagueChangeRepresentative
 */
export const useSetUserColleagueChangeRepresentative = () => {
  const queryClient = useQueryClient();

  const { mutate: userColleagueChangeRepresentativeFetchMutate, isLoading: userColleagueChangeRepresentativeIsLoading } = useMutation(
    ['userColleagueChangeRepresentative'],
    async ({ substitute_id }: { substitute_id: string }) => {
      const result = await userColleagueChangeRepresentativeApi({ substitute_id });
      return result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user']);
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    userColleagueChangeRepresentativeFetchMutate,
    userColleagueChangeRepresentativeIsLoading,
  };
};

/**
 * 동료 목록 조회 API
 * @function useGetUserColleaguesFetch
 */
export const useGetUserColleaguesFetch = () => {
  const {
    data: getUserColleaguesFetchData,
    refetch: getUserColleaguesRefetch,
    isLoading: getUserColleaguesIsLoading,
  } = useQuery(['getUserColleagues'], () => userColleagueGetColleagueListApi());
  return {
    getUserColleaguesFetchData,
    getUserColleaguesRefetch,
    getUserColleaguesIsLoading,
  };
};

/**
 * 공급업체 제품 옵션 생성 API
 * @function useSetUserSupplierProductOption
 */
export const useSetUserSupplierProductOption = () => {
  const queryClient = useQueryClient()

  const { mutate: userSupplierProductOptionFetchMutate, isLoading: userSupplierProductOptionIsLoading } = useMutation(
    ['userSupplierProductOption'],
    async (product_option: string) => {
      const result = await userSupplierProductOptionApi(product_option)
      return result
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userSupplierProductOption'])
      },
      onError: (err: AxiosError<CommonAxiosErrorType>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text)
        }
      }
    }
  )

  return {
    userSupplierProductOptionFetchMutate,
    userSupplierProductOptionIsLoading
  }
}